/**
 * TrackingTagRulesPanel — first-touch attribution tag rules management.
 * Configure URL-based rules that auto-fire events:
 *   !clicked → page_view (default, always active)
 *   !lead → lead (when URL matches or form submitted)
 *   !call → booked_call (when booking page detected)
 *   !sale → purchase (from Stripe/manual)
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Plus,
  Trash2,
  Check,
  X,
  Tag,
  Globe,
  MousePointerClick,
  Phone,
  DollarSign,
  FileText,
  Pencil,
  Power,
} from "lucide-react";

interface TagRule {
  id: string;
  userId: string;
  tag: string;
  triggerType: string;
  triggerValue: string | null;
  eventName: string;
  autoIdentify: boolean;
  revenue: string | null;
  currency: string | null;
  enabled: boolean;
  priority: number;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

const EVENT_NAMES = [
  { value: "page_view", label: "Page View", icon: "👁️", tag: "!clicked" },
  { value: "lead", label: "Lead", icon: "📧", tag: "!lead" },
  { value: "booked_call", label: "Booked Call", icon: "📞", tag: "!call" },
  { value: "qualified_call", label: "Qualified Call", icon: "✅", tag: "!qualified" },
  { value: "checkout_started", label: "Checkout Started", icon: "🛒", tag: "!checkout" },
  { value: "purchase", label: "Purchase", icon: "💰", tag: "!sale" },
  { value: "upsell", label: "Upsell", icon: "⬆️", tag: "!upsell" },
  { value: "refund", label: "Refund", icon: "↩️", tag: "!refund" },
] as const;

const TRIGGER_TYPES = [
  {
    value: "url_contains",
    labelKey: "tagRules.trigger.url_contains.label",
    descKey: "tagRules.trigger.url_contains.desc",
  },
  {
    value: "url_exact",
    labelKey: "tagRules.trigger.url_exact.label",
    descKey: "tagRules.trigger.url_exact.desc",
  },
  {
    value: "url_regex",
    labelKey: "tagRules.trigger.url_regex.label",
    descKey: "tagRules.trigger.url_regex.desc",
  },
  {
    value: "form_submit",
    labelKey: "tagRules.trigger.form_submit.label",
    descKey: "tagRules.trigger.form_submit.desc",
  },
] as const;

function TagBadge({ tag }: { tag: string }) {
  const colors: Record<string, string> = {
    "!clicked": "bg-blue-500/20 text-blue-300 border-blue-500/30",
    "!lead": "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    "!call": "bg-purple-500/20 text-purple-300 border-purple-500/30",
    "!qualified": "bg-indigo-500/20 text-indigo-300 border-indigo-500/30",
    "!checkout": "bg-amber-500/20 text-amber-300 border-amber-500/30",
    "!sale": "bg-red-500/20 text-red-300 border-red-500/30",
    "!upsell": "bg-pink-500/20 text-pink-300 border-pink-500/30",
    "!refund": "bg-gray-500/20 text-gray-300 border-gray-500/30",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono border ${colors[tag] || "bg-white/10 text-white/70 border-white/20"}`}
    >
      <Tag className="w-3 h-3 mr-1" />
      {tag}
    </span>
  );
}

function CreateRuleForm({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const [tag, setTag] = useState("!lead");
  const [triggerType, setTriggerType] = useState("url_contains");
  const [triggerValue, setTriggerValue] = useState("");
  const [eventName, setEventName] = useState("lead");
  const [revenue, setRevenue] = useState("");
  const [description, setDescription] = useState("");

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiRequest("POST", "/api/tracking/tag-rules/manage", data),
    onSuccess: onCreated,
  });

  const handleEventChange = (ev: string) => {
    setEventName(ev);
    const preset = EVENT_NAMES.find((e) => e.value === ev);
    if (preset) setTag(preset.tag);
  };

  return (
    <div className="p-3 bg-white/[0.03] border border-white/[0.1] rounded-lg space-y-3">
      <h4 className="text-xs font-medium text-white/80 flex items-center gap-1.5">
        <Plus className="w-3.5 h-3.5 text-red-400" /> {t("tagRules.create.title")}
      </h4>

      {/* Event type → Tag */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-white/50 mb-1 block">
            {t("tagRules.create.eventType")}
          </label>
          <select
            value={eventName}
            onChange={(e) => handleEventChange(e.target.value)}
            className="w-full h-8 px-2 text-xs bg-[#030407] border border-white/[0.1] rounded-md text-white"
          >
            {EVENT_NAMES.filter((e) => e.value !== "page_view").map((e) => (
              <option key={e.value} value={e.value}>
                {e.icon} {e.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-white/50 mb-1 block">{t("tagRules.create.tag")}</label>
          <input
            type="text"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            placeholder="!lead"
            className="w-full h-8 px-2 text-xs bg-[#030407] border border-white/[0.1] rounded-md text-white font-mono"
          />
        </div>
      </div>

      {/* Trigger */}
      <div>
        <label className="text-xs text-white/50 mb-1 block">
          {t("tagRules.create.triggerCondition")}
        </label>
        <select
          value={triggerType}
          onChange={(e) => setTriggerType(e.target.value)}
          className="w-full h-8 px-2 text-xs bg-[#030407] border border-white/[0.1] rounded-md text-white"
        >
          {TRIGGER_TYPES.map((tr) => (
            <option key={tr.value} value={tr.value}>
              {t(tr.labelKey)} — {t(tr.descKey)}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="text-xs text-white/50 mb-1 block">
          {triggerType === "form_submit"
            ? t("tagRules.create.formUrl")
            : t("tagRules.create.urlPattern")}
        </label>
        <input
          type="text"
          value={triggerValue}
          onChange={(e) => setTriggerValue(e.target.value)}
          placeholder={
            triggerType === "url_contains"
              ? "/thank-you"
              : triggerType === "url_regex"
                ? "/(thank|dziekuj)"
                : "/checkout"
          }
          className="w-full h-8 px-2 text-xs bg-[#030407] border border-white/[0.1] rounded-md text-white placeholder:text-white/20"
        />
      </div>

      {/* Revenue (for !sale) */}
      {(eventName === "purchase" || eventName === "upsell") && (
        <div>
          <label className="text-xs text-white/50 mb-1 block">
            {t("tagRules.create.fixedRevenue")}
          </label>
          <input
            type="number"
            value={revenue}
            onChange={(e) => setRevenue(e.target.value)}
            placeholder="997"
            className="w-full h-8 px-2 text-xs bg-[#030407] border border-white/[0.1] rounded-md text-white placeholder:text-white/20"
          />
        </div>
      )}

      {/* Description */}
      <div>
        <label className="text-xs text-white/50 mb-1 block">
          {t("tagRules.create.description")}
        </label>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t("tagRules.create.descriptionPlaceholder")}
          className="w-full h-8 px-2 text-xs bg-[#030407] border border-white/[0.1] rounded-md text-white placeholder:text-white/20"
        />
      </div>

      <div className="flex gap-2 pt-1">
        <Button
          size="sm"
          className="bg-red-600 hover:bg-red-700 text-white text-xs flex-1"
          disabled={createMutation.isPending || !tag}
          onClick={() =>
            createMutation.mutate({
              tag,
              triggerType,
              triggerValue: triggerValue || null,
              eventName,
              revenue: revenue ? parseFloat(revenue) : null,
              description: description || null,
            })
          }
        >
          {createMutation.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
          ) : (
            <Plus className="h-3 w-3 mr-1" />
          )}
          {t("tagRules.create.add")}
        </Button>
        <Button size="sm" variant="ghost" className="text-xs text-white/50" onClick={onCancel}>
          {t("tagRules.create.cancel")}
        </Button>
      </div>
      {createMutation.isError && (
        <p className="text-xs text-amber-400/70">{t("tagRules.create.tryAgain")}</p>
      )}
    </div>
  );
}

export default function TrackingTagRulesPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading } = useQuery<{ rules: TagRule[] }>({
    queryKey: ["tracking-tag-rules"],
    queryFn: () => apiRequest("GET", "/api/tracking/tag-rules/manage"),
    staleTime: 30_000,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      apiRequest("PUT", `/api/tracking/tag-rules/manage/${id}`, { enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tracking-tag-rules"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/tracking/tag-rules/manage/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tracking-tag-rules"] }),
  });

  const rules = data?.rules || [];

  // Default !clicked rule (always active, not stored in DB)
  const defaultRules = [
    { tag: "!clicked", triggerKey: "tagRules.builtin.clicked" },
    { tag: "!lead", triggerKey: "tagRules.builtin.lead" },
    { tag: "!call", triggerKey: "tagRules.builtin.call" },
  ];

  if (isLoading) {
    return (
      <div className="p-3 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-red-400" />
        <span className="text-sm text-white/60">{t("tagRules.loading")}</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-medium text-white/70 flex items-center gap-1.5">
          <Tag className="w-3.5 h-3.5" /> {t("tagRules.title")}
        </h3>
        {!showCreate && (
          <Button
            size="sm"
            variant="ghost"
            className="text-xs text-red-400 hover:text-red-300 h-6 px-2"
            onClick={() => setShowCreate(true)}
          >
            <Plus className="w-3 h-3 mr-1" /> {t("tagRules.addRule")}
          </Button>
        )}
      </div>

      {/* Info */}
      <div className="p-2.5 bg-blue-500/10 border border-blue-500/20 rounded-lg">
        <p className="text-xs text-blue-300 leading-relaxed">
          <strong>{t("tagRules.howItWorks.label")}</strong> {t("tagRules.howItWorks.text")}
        </p>
      </div>

      {/* Built-in rules */}
      <div className="space-y-1">
        <p className="text-xs text-white/40 font-medium">{t("tagRules.builtinTitle")}</p>
        {defaultRules.map((rule) => (
          <div
            key={rule.tag}
            className="flex items-center gap-2 p-2 bg-white/[0.02] rounded-md border border-white/[0.06]"
          >
            <TagBadge tag={rule.tag} />
            <div className="flex-1 min-w-0">
              <span className="text-xs text-white/60">{t(rule.triggerKey)}</span>
            </div>
            <Badge className="text-[10px] bg-green-600/20 text-green-400 border-green-500/50">
              {t("tagRules.auto")}
            </Badge>
          </div>
        ))}
      </div>

      {/* Custom rules */}
      {rules.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs text-white/40 font-medium">{t("tagRules.customTitle")}</p>
          {rules.map((rule) => (
            <div
              key={rule.id}
              className={`flex items-center gap-2 p-2 rounded-md border transition-colors ${
                rule.enabled
                  ? "bg-white/[0.03] border-white/[0.08]"
                  : "bg-white/[0.01] border-white/[0.04] opacity-50"
              }`}
            >
              <TagBadge tag={rule.tag} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <span className="text-xs text-white/70">
                    {(() => {
                      const tt = TRIGGER_TYPES.find((x) => x.value === rule.triggerType);
                      return tt ? t(tt.labelKey) : rule.triggerType;
                    })()}
                  </span>
                  {rule.triggerValue && (
                    <code className="text-xs text-emerald-400 bg-[#030407] px-1 py-0.5 rounded truncate max-w-[200px]">
                      {rule.triggerValue}
                    </code>
                  )}
                </div>
                {rule.description && (
                  <p className="text-[10px] text-white/40 mt-0.5 truncate">{rule.description}</p>
                )}
                {rule.revenue && (
                  <span className="text-[10px] text-amber-400">
                    {t("tagRules.value", {
                      revenue: rule.revenue,
                      currency: rule.currency || "PLN",
                    })}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => toggleMutation.mutate({ id: rule.id, enabled: !rule.enabled })}
                  className={`p-1 rounded hover:bg-white/[0.06] transition-colors ${
                    rule.enabled ? "text-green-400" : "text-white/30"
                  }`}
                  title={rule.enabled ? t("tagRules.disable") : t("tagRules.enable")}
                >
                  <Power className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => {
                    if (confirm(t("tagRules.confirmDelete", { tag: rule.tag }))) {
                      deleteMutation.mutate(rule.id);
                    }
                  }}
                  className="p-1 rounded hover:bg-red-500/20 text-white/30 hover:text-red-400 transition-colors"
                  title={t("tagRules.delete")}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <CreateRuleForm
          onCreated={() => {
            setShowCreate(false);
            queryClient.invalidateQueries({ queryKey: ["tracking-tag-rules"] });
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {/* Example rules hint */}
      {rules.length === 0 && !showCreate && (
        <div className="p-2.5 bg-white/[0.02] border border-dashed border-white/[0.1] rounded-lg">
          <p className="text-xs text-white/40 mb-2">{t("tagRules.examples.title")}</p>
          <div className="space-y-1 text-xs text-white/50">
            <div className="flex items-center gap-2">
              <TagBadge tag="!lead" />
              <span>
                {t("tagRules.examples.lead")}{" "}
                <code className="text-emerald-400/60">/thank-you</code>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <TagBadge tag="!call" />
              <span>
                {t("tagRules.examples.call")}{" "}
                <code className="text-emerald-400/60">/booking-confirmed</code>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <TagBadge tag="!sale" />
              <span>
                {t("tagRules.examples.sale")}{" "}
                <code className="text-emerald-400/60">/checkout-success</code>{" "}
                {t("tagRules.examples.saleSuffix")}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
